// External UI
import { Button, Checkbox, Loader, Select, Text, TextInput } from '@mantine/core'
import { ResourceInput } from '@medplum/react'

// External utilities
import { useForm, useWatch, Controller, FieldErrors } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { createReference, WithId } from '@medplum/core'
import { useEffect, useRef, useState } from 'react'
import { DocumentReference, Patient, Practitioner, Reference } from '@medplum/fhirtypes'

// Local hooks and types
import { logError } from '@/utils/errorUtils'
import { SNOMED_USER_ROLES } from '@/utils'
import {
  faxTriageSchema,
  useFaxTriage,
  type FaxDocumentType,
  type FaxTriageParams,
} from '@/hooks/useFaxTriage'
import type { OcrData } from '@/hooks/useFaxInbox'
import { useFaxPatientCheck, useFaxPatientReview } from '@/hooks/useFaxPatientCheck'
import { FaxPatientReviewConfirm, FaxPatientWarnings } from './FaxPatientWarnings'

export function FaxTriagePanel({
  documentReference,
  taskId,
  disabled = false,
  defaultPatient,
  suggestedPatient,
  defaultTitle,
  defaultReferringProvider,
  defaultLabName,
  defaultDocumentType = 'lab',
  ocrData,
  checkPatients = false,
  onDirtyChange,
}: {
  documentReference: WithId<DocumentReference>
  taskId: string
  disabled?: boolean
  defaultPatient?: Reference<Patient>
  // Pre-filled once into an empty Related patient; staff can clear it and it stays cleared.
  suggestedPatient?: Reference<Patient>
  defaultTitle?: string
  defaultReferringProvider?: Reference<Practitioner>
  defaultLabName?: string
  defaultDocumentType?: FaxDocumentType
  ocrData: OcrData
  // OCR read who is on each page: warn when the chosen patient isn't the one the fax names.
  checkPatients?: boolean
  // Lets the parent avoid swapping this form out while staff are mid-entry.
  onDirtyChange?: (dirty: boolean) => void
}) {
  const faxTriage = useFaxTriage(documentReference, taskId)

  const form = useForm<FaxTriageParams>({
    resolver: zodResolver(faxTriageSchema),
    defaultValues: {
      documentType: defaultDocumentType,
      assignedPatient: defaultPatient,
      title: defaultTitle ?? '',
      referringProvider: defaultReferringProvider,
      labName: defaultLabName ?? ocrData.labName ?? '',
      notifyPatient: true,
    },
  })

  // Lab, Referring provider and the follow-up email belong to Labs/Imaging only.
  const isLab = useWatch({ control: form.control, name: 'documentType' }) === 'lab'
  const patientId = useWatch({ control: form.control, name: 'assignedPatient' })
    ?.reference?.split('/')
    .pop()
  const checkGroups = patientId ? [{ patientId }] : []
  const patientCheck = useFaxPatientCheck(taskId, checkGroups, checkPatients && !disabled)
  const patientReview = useFaxPatientReview(taskId)
  // A disabled query keeps its last answer: a filed fax shows no check.
  const checked = checkPatients && !disabled ? patientCheck.data : undefined
  const needsReview = !!checked?.needsReview
  // A confirmation covers the warnings staff saw: a new answer (another patient) needs a new one.
  const [reviewConfirmed, setReviewConfirmed] = useState(false)
  useEffect(() => setReviewConfirmed(false), [checked])

  useEffect(() => {
    if (ocrData.status === 'completed' && ocrData.labName && !form.getValues('labName')) {
      form.setValue('labName', ocrData.labName, { shouldDirty: false })
    }
  }, [ocrData, form])

  const suggestionSettled = useRef(false)
  useEffect(() => {
    const reference = suggestedPatient?.reference
    if (!reference || suggestionSettled.current) return
    suggestionSettled.current = true
    if (disabled || form.getValues('assignedPatient')) return
    form.setValue('assignedPatient', { reference }, { shouldDirty: false })
  }, [suggestedPatient, disabled, form])

  const { isDirty, isSubmitting } = form.formState
  // Submit files the values as they were when clicked: no edits while it is on its way.
  const locked = disabled || isSubmitting
  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  const onSubmit = async (data: FaxTriageParams) => {
    try {
      if (needsReview) await patientReview.mutateAsync(checkGroups)
      await faxTriage.mutateAsync(data)
    } catch {
      // The hooks already showed staff why; keep the form as they left it.
      return
    }

    form.reset(data)
  }

  const onError = (errors: FieldErrors<FaxTriageParams>) => {
    logError('fax', 'Fax triage form validation failed', new Error(JSON.stringify(errors)))
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit, onError)} className="flex flex-col gap-2">
      <OcrSuggestions ocrData={ocrData} />
      <Controller
        name="documentType"
        control={form.control}
        render={({ field, fieldState }) => (
          <Select
            label="Document type"
            error={fieldState.error?.message}
            data={[
              { label: 'Labs/Imaging', value: 'lab' },
              { label: 'Document', value: 'document' },
            ]}
            disabled={locked || field.disabled}
            {...field}
          />
        )}
      />
      <Controller
        name="title"
        control={form.control}
        render={({ field, fieldState }) => (
          <TextInput
            label="Title"
            error={fieldState.error?.message}
            disabled={locked || field.disabled}
            {...field}
          />
        )}
      />
      {isLab && (
        <Controller
          name="labName"
          control={form.control}
          render={({ field, fieldState }) => (
            <TextInput
              label="Lab"
              placeholder="Lab name"
              error={fieldState.error?.message}
              disabled={locked || field.disabled}
              {...field}
            />
          )}
        />
      )}
      <Controller
        name="assignedPatient"
        control={form.control}
        render={({ field, fieldState }) => (
          <ResourceInput
            // defaultValue is uncontrolled: remount so a suggestion set after mount shows.
            key={field.value?.reference ?? 'none'}
            label="Related patient"
            resourceType="Patient"
            name={field.name}
            error={fieldState.error?.message}
            disabled={locked || field.disabled}
            defaultValue={field.value}
            onChange={(e) => {
              if (e) {
                field.onChange(createReference(e))
              } else {
                field.onChange(null)
              }
            }}
          />
        )}
      />
      {isLab && (
        <>
          <Controller
            name="referringProvider"
            control={form.control}
            render={({ field }) => (
              <ResourceInput
                label="Referring provider"
                resourceType="Practitioner"
                name={field.name}
                disabled={locked || field.disabled}
                defaultValue={field.value ?? undefined}
                searchCriteria={{
                  '_has:PractitionerRole:practitioner:role': SNOMED_USER_ROLES.MEDICAL_DOCTOR.code,
                }}
                onChange={(e) => {
                  if (e) {
                    field.onChange(createReference(e))
                  } else {
                    field.onChange(null)
                  }
                }}
              />
            )}
          />

          <Controller
            name="notifyPatient"
            control={form.control}
            render={({ field }) => (
              <Checkbox
                label="Email the patient an invitation to schedule a follow-up"
                checked={field.value}
                onChange={(e) => field.onChange(e.currentTarget.checked)}
                disabled={locked || field.disabled}
              />
            )}
          />
        </>
      )}

      <FaxPatientWarnings group={checked?.groups[0]} />
      {needsReview && (
        <FaxPatientReviewConfirm
          checked={reviewConfirmed}
          onChange={setReviewConfirmed}
          disabled={locked}
        />
      )}

      <Button
        loading={form.formState.isSubmitting}
        disabled={disabled || !form.formState.isDirty || (needsReview && !reviewConfirmed)}
        type="submit"
      >
        Submit
      </Button>
    </form>
  )
}

function OcrSuggestions({ ocrData }: { ocrData: OcrData }) {
  return (
    <div className="rounded border border-gray-200 bg-gray-50 p-3">
      {ocrData.status === 'pending' && (
        <div className="flex items-center gap-2">
          <Loader size={12} />
          <Text size="xs" c="dimmed">
            Extracting fields...
          </Text>
        </div>
      )}

      {ocrData.status === 'not-run' && (
        <Text size="xs" c="dimmed">
          OCR was not run on this fax
        </Text>
      )}

      {ocrData.status === 'failed' && (
        <Text size="xs" c="dimmed">
          OCR unavailable
        </Text>
      )}

      {ocrData.status === 'completed' && (
        <div className="flex flex-col gap-1">
          <OcrField label="Lab" value={ocrData.labName} />
          <OcrField label="Patient" value={ocrData.patientName} />
          <OcrField label="Provider" value={ocrData.practitionerName} />
        </div>
      )}
    </div>
  )
}

interface OcrFieldProps {
  label: string
  value: string | null
}

function OcrField({ label, value }: OcrFieldProps) {
  return (
    <div className="flex gap-2">
      <Text size="xs" c="dimmed" className="w-16 shrink-0">
        {label}
      </Text>
      <Text size="xs" c={value ? undefined : 'dimmed'}>
        {value ?? '—'}
      </Text>
    </div>
  )
}
